// Chapter 16 - Motivating Operations
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 16 (Michael & Miguel).
// All content traced to source-pdfs/Chapter-16-Motivating-Operations-5.pdf

import type { ChapterModule } from "./types";

const chapter16Module: ChapterModule = {
  meta: {
    id: "16",
    number: 16,
    title: "Motivating Operations",
    subtitle: "EO/AO, value-altering and behavior-altering effects",
    part: "Part 6 - Antecedent Variables",
    estMinutes: 80,
    bacb: ["B-12", "G-2"],
  },
  curated: true,
  overview: [
    "Chapter 16 (Michael & Miguel) gives motivating operations (MOs) the full treatment. An MO is an environmental variable that (a) alters the EFFECTIVENESS of some stimulus as a reinforcer or punisher (value-altering effect) and (b) alters the current frequency of all behavior that has been consequated by that stimulus (behavior-altering effect).",
    "The chapter distinguishes EOs from AOs, establishes the SD/MO distinction (SDs signal availability; MOs make you WANT something), introduces unconditioned MOs (UMOs) for deprivation, satiation, painful stimulation, and temperature, and explains the three conditioned MO (CMO) types: surrogate (CMO-S), reflexive (CMO-R), and transitive (CMO-T). It closes with the practical implications: MOs modulate three-term contingencies, drive generalization failures, and are routinely manipulated in mand training via interrupted chains.",
  ],
  objectives: [
    { id: "B-12", text: "Define and provide examples of motivating operations (EO, AO, value- and behavior-altering effects)." },
    { id: "G-2",  text: "Use motivating operations procedurally to support skill acquisition and reduce challenging behavior." },
  ],
  coreConcepts: [
    {
      title: "Definition: MO has two effects",
      body: [
        "A motivating operation (MO) (a) alters the EFFECTIVENESS of some stimulus as a reinforcer (value-altering effect) and (b) alters the current FREQUENCY of all behavior that has been reinforced by that stimulus (behavior-altering effect). Both effects occur SIMULTANEOUSLY but are independent in principle (one does not derive from the other).",
      ],
    },
    {
      title: "Value-altering: EO vs AO",
      body: [
        "An ESTABLISHING OPERATION (EO) INCREASES reinforcer effectiveness. An ABOLISHING OPERATION (AO) DECREASES reinforcer effectiveness. Food deprivation is an EO for food; satiation is an AO for food.",
      ],
    },
    {
      title: "Behavior-altering: evocative vs abative",
      body: [
        "The behavior-altering effect is either an EVOCATIVE effect (increase in current frequency of behavior consequated by the stimulus) or an ABATIVE effect (decrease in current frequency).",
      ],
      bullets: [
        "EO: value-altering = INCREASE effectiveness; behavior-altering = EVOCATIVE.",
        "AO: value-altering = DECREASE effectiveness; behavior-altering = ABATIVE.",
      ],
    },
    {
      title: "MO indirectly affects SDs",
      body: [
        "The behavior-altering effect of an MO can directly increase or decrease the frequency of behavior AND indirectly affect the evocative or abative strength of relevant SDs. The value-altering effect alters the reinforcing effectiveness of the relevant consequence AND any conditioned stimulus correlated with it.",
      ],
    },
    {
      title: "SD vs MO (the critical distinction)",
      body: [
        "SDs are related to the differential AVAILABILITY of a currently effective reinforcer. MOs are related to the differential EFFECTIVENESS of a particular event. Most MOs fail the SD test because in the absence of the MO there is no reinforcer in play to be unavailable.",
      ],
      bullets: [
        "SD = 'something you want is available.'",
        "MO = 'something is made wantable.'",
      ],
    },
    {
      title: "Unconditioned MOs (UMOs)",
      body: [
        "UMOs alter reinforcer value without prior learning. The main UMOs for humans involve deprivation/satiation with food, water, oxygen, activity, and sleep; sexual reinforcement; comfortable temperature; and painful stimulation. Each variable has both an EO and an AO, and each carries evocative and abative effects.",
      ],
    },
    {
      title: "Surrogate CMO (CMO-S)",
      body: [
        "A CMO-S acquires its MO effectiveness by being paired with another MO and has the same value- and behavior-altering effects as the paired MO. Example: seeing a restaurant after a meal but wanting to eat again; the restaurant has been paired with food deprivation in the past, so it now functions like an MO for food.",
      ],
    },
    {
      title: "Reflexive CMO (CMO-R)",
      body: [
        "A CMO-R acquires MO effectiveness by preceding some form of WORSENING or IMPROVEMENT. The warning stimulus in escape-avoidance is the canonical CMO-R: it establishes its own offset as reinforcement and evokes all behavior that has accomplished that offset. The CMO-R is OFTEN MISIDENTIFIED as an SD; it fails the SD test because in its absence there is no reinforcer unavailability.",
      ],
      bullets: [
        "A CMO-R that has preceded improvement establishes its own offset as a punisher and abates behavior that has been so punished.",
        "Many everyday social interactions involve unrecognized CMO-Rs (e.g., being asked a question initiates a brief period of social awkwardness; the question functions as a warning stimulus).",
      ],
    },
    {
      title: "Transitive CMO (CMO-T)",
      body: [
        "A CMO-T establishes (or abolishes) the reinforcing effectiveness of ANOTHER stimulus and evokes (or abates) behavior reinforced by that other stimulus. The classic application is the interrupted-chain procedure for mand training: withholding an item the learner needs to complete a task creates a CMO-T that makes the item more reinforcing in the moment, creating the ideal teaching condition.",
      ],
      bullets: [
        "Variables that function as UMOs also function as CMO-Ts for conditioned reinforcers related to the unconditioned reinforcer (e.g., food deprivation makes utensils more reinforcing as CMO-Ts).",
        "CMO-Ts evoke behavior because of their relation to the VALUE of a consequence, not to its availability.",
      ],
    },
    {
      title: "MOs for punishment",
      body: [
        "MOs alter the punishing effectiveness of a stimulus AND the frequency of behaviors that have been punished by that stimulus. EO for punishment = punisher-establishing + abative effect. AO for punishment = punisher-abolishing + evocative effect. Conditioned punishers established via removal of reinforcers depend on the same MOs that establish the value of those reinforcers. Social disapproval, time-out, and response cost function as punishment partly because they reduce availability of reinforcers; their effectiveness depends on the MOs for those reinforcers.",
      ],
    },
    {
      title: "Multiple effects of MOs (and MO/consequence overlap)",
      body: [
        "Most environmental events have more than one effect. UMOs typically have behavior-altering effects on current frequency AND function-altering effects on future frequency when presented as consequences. Painful stimulation is an MO with evocative effects AND a punisher for behavior immediately preceding its onset. Food consumption is an AO AND a reinforcer for the response that produced it.",
      ],
    },
    {
      title: "Why MOs matter for generalization and treatment",
      body: [
        "An acquired behavior may not occur in a new setting if the relevant MO is absent. Generalization failures can come from novel stimuli AND from missing MOs. When programming generalization, clinicians must program across different SDs AND across different MOs. The three-term contingency is modulated by a fourth variable (the MO) that determines reinforcer effectiveness and the evocative strength of SDs.",
      ],
    },
  ],
  glossary: [
    { term: "Motivating operation (MO)", definition: "Environmental variable that (a) alters the reinforcing/punishing effectiveness of some stimulus and (b) alters the current frequency of all behavior consequated by that stimulus.", sourceHint: "A motivating operation (MO) (a) alters the effectiveness of some stimulus as a reinforcer, the value-altering effect; and (b) alters the current frequency of all behavior that has been reinforced by that stimulus, the behavior-altering effect." },
    { term: "Establishing operation (EO)", definition: "MO that INCREASES reinforcer effectiveness; has an evocative effect.", sourceHint: "an increase in the reinforcing effectiveness of some stimulus, in which case the MO is an establishing operation (EO)." },
    { term: "Abolishing operation (AO)", definition: "MO that DECREASES reinforcer effectiveness; has an abative effect.", sourceHint: "a decrease in reinforcing effectiveness, in which case the MO is an abolishing operation (AO)." },
    { term: "Value-altering effect", definition: "The MO's effect on the current effectiveness of a reinforcer or punisher.", sourceHint: "the value-altering effect of an MO changes the potency of reinforcers (or punishers) to increase and maintain (or decrease and eliminate) behavior." },
    { term: "Behavior-altering effect", definition: "The MO's effect on the current frequency of behavior consequated by the relevant stimulus; evocative or abative.", sourceHint: "The behavior-altering effect is either (a) an increase in the current frequency of behavior that has been reinforced by some stimulus, called an evocative effect; or (b) a decrease in the current frequency of behavior that has been reinforced by some stimulus, called an abative effect." },
    { term: "Evocative effect", definition: "Increase in the current frequency of behavior consequated by the relevant stimulus.", sourceHint: "an increase in the current frequency of behavior... called an evocative effect." },
    { term: "Abative effect", definition: "Decrease in the current frequency of behavior consequated by the relevant stimulus.", sourceHint: "a decrease in the current frequency of behavior... called an abative effect." },
    { term: "Unconditioned motivating operation (UMO)", definition: "MO whose value-altering effect does not depend on a learning history (e.g., food/water/sleep/temperature/pain).", sourceHint: "Events, operations, and stimulus conditions with unconditioned value-altering effects... These MOs establish or abolish the value of stimuli as reinforcers in the absence of prior learning." },
    { term: "Conditioned motivating operation (CMO)", definition: "MO whose effectiveness depends on the organism's learning history.", sourceHint: "Motivating variables that alter the reinforcing effectiveness of other stimuli, objects, or events, but only as a result of the organism's learning history, are called conditioned motivating operations (CMOs)." },
    { term: "Surrogate CMO (CMO-S)", definition: "A stimulus that acquires MO effectiveness by being paired with another MO; produces the same effects as the paired MO.", sourceHint: "The surrogate CMO (CMO-S) is a stimulus that acquires its MO effectiveness by being paired with another MO, and has the same value-altering and behavior-altering effects as the MO with which it was paired." },
    { term: "Reflexive CMO (CMO-R)", definition: "A stimulus that acquires MO effectiveness by preceding some form of worsening or improvement; the warning stimulus in escape-avoidance is the canonical example.", sourceHint: "A stimulus that acquires MO effectiveness by preceding some form of worsening or improvement is called a reflexive CMO (CMO-R)." },
    { term: "Transitive CMO (CMO-T)", definition: "An environmental variable that establishes (or abolishes) the reinforcing effectiveness of ANOTHER stimulus and evokes (or abates) behavior reinforced by that other stimulus.", sourceHint: "An environmental variable that establishes (or abolishes) the reinforcing effectiveness of another stimulus and evokes (or abates) the behavior that has been reinforced by that other stimulus is a transitive CMO, or CMO-T." },
    { term: "Interrupted-chain procedure", definition: "Mand-training procedure that withholds an item the learner needs to complete a previously learned task, functioning as a CMO-T.", sourceHint: "The typical procedure, termed interrupted-chain, involves withholding an item the learner needs to complete a previously learned task. This manipulation functions as a CMO-T, increasing the value of the item as a reinforcer." },
    { term: "Function-altering effect", definition: "Effect on the FUTURE frequency of behavior, distinct from the current-frequency behavior-altering effect of an MO.", sourceHint: "Reinforcers, punishers, and response occurrence without consequence alter the future frequency of behavior (function-altering effect)." },
  ],
  procedures: [
    {
      title: "Identify an MO in an applied scenario",
      intro: "Differentiate from SDs and pinpoint EO/AO with evocative/abative effect.",
      steps: [
        { title: "Specify the consequence", detail: "What reinforcer or punisher might be at work?" },
        { title: "Identify the relevant operation", detail: "Is something making the reinforcer more or less effective right now (deprivation, satiation, pairing history)?" },
        { title: "Check SD test", detail: "Is the reinforcer differentially AVAILABLE based on this stimulus? If yes, it might be an SD. If the absence of the stimulus simply means there is no current MO, it is an MO." },
        { title: "Classify as EO or AO", detail: "Does the MO increase (EO) or decrease (AO) the reinforcer's value?" },
        { title: "Predict evocative/abative effect", detail: "EOs evoke; AOs abate. Match the predicted change in current frequency." },
      ],
    },
    {
      title: "Use a CMO-T in mand training (interrupted chain)",
      intro: "Manipulate motivation in real time to teach functional requests.",
      steps: [
        { title: "Identify a high-value chain", detail: "Pick a task the learner reliably completes and a critical item required to complete it." },
        { title: "Withhold the item", detail: "Set up the situation so the chain cannot continue without the item." },
        { title: "Allow the MO to peak", detail: "Wait for the value-altering effect to establish the item as a current reinforcer." },
        { title: "Prompt the mand", detail: "Prompt the learner to request the item; reinforce with the actual item." },
        { title: "Fade prompts", detail: "Gradually reduce prompts; ensure the mand generalizes across CMO-T setups." },
      ],
    },
    {
      title: "Program MOs into generalization",
      intro: "Don't just program SD variability; program MO variability.",
      steps: [
        { title: "List relevant MOs", detail: "Identify the MOs present during training (deprivation states, schedule effects, social context)." },
        { title: "Vary MOs across teaching", detail: "Conduct probes in conditions where the trained MO is absent and other MOs are present." },
        { title: "Pair the response with multiple MOs", detail: "Teach the response under deprivation, satiation, and various CMO conditions where appropriate." },
        { title: "Measure across MOs", detail: "Track generalization separately by MO condition; revise if responding fails under certain MO conditions." },
      ],
    },
  ],
  examples: [
    { scenario: "A child has not eaten for 4 hours; food is delivered immediately when the child requests it; future requests increase.",
      application: "Food deprivation is a UMO (EO) for food. Evocative effect: immediate increase in food-related behavior. Value-altering effect: food is currently more reinforcing.",
      sourceHint: "food deprivation is an EO and has evocative effects on relevant behavior, and food ingestion is an AO and has abative effects." },
    { scenario: "Walking past a restaurant after dinner triggers a craving despite recent satiation.",
      application: "Surrogate CMO (CMO-S). The restaurant has been paired with food deprivation in the past, so it temporarily establishes food as more reinforcing.",
      sourceHint: "The CMO-S appears to play a role in the development of behaviors that do not seem to make much sense, such as seeing a restaurant after dinner and wanting to eat again." },
    { scenario: "A flashing warning light precedes a loud noise; a button press turns the light (and noise) off; future button presses increase.",
      application: "Reflexive CMO (CMO-R). The light's onset is itself aversive (because it predicts the noise); its offset reinforces button pressing.",
      sourceHint: "A stimulus that acquires MO effectiveness by preceding some form of worsening or improvement is called a reflexive CMO (CMO-R)." },
    { scenario: "An adult removes the spoon a child needs to eat their yogurt and waits for the child to ask 'spoon.'",
      application: "Transitive CMO (CMO-T) via interrupted chain. Withholding the spoon makes 'spoon' a more potent reinforcer in the moment, evoking the mand.",
      sourceHint: "The manipulation of CMO-Ts is common practice when teaching mands to individuals with disabilities. The typical procedure, termed interrupted-chain, involves withholding an item the learner needs to complete a previously learned task." },
  ],
  comparisons: [
    {
      title: "SD vs MO",
      left:  { label: "SD", points: [
        "Signals AVAILABILITY of a currently effective reinforcer.",
        "'Something you want is available.'",
        "Defined by reinforcer availability vs unavailability.",
      ]},
      right: { label: "MO", points: [
        "Alters the EFFECTIVENESS of a reinforcer.",
        "'Something is made wantable.'",
        "Defined by reinforcer value not by availability.",
      ]},
    },
    {
      title: "EO vs AO",
      left:  { label: "EO", points: [
        "INCREASES reinforcer effectiveness.",
        "Evocative effect: current frequency rises.",
        "Example: food deprivation.",
      ]},
      right: { label: "AO", points: [
        "DECREASES reinforcer effectiveness.",
        "Abative effect: current frequency falls.",
        "Example: food satiation.",
      ]},
    },
    {
      title: "CMO-S, CMO-R, CMO-T",
      left:  { label: "CMO-S (Surrogate)", points: [
        "Paired with another MO.",
        "Has the same effects as the paired MO.",
        "Example: restaurant after meal triggers craving.",
      ]},
      right: { label: "CMO-R / CMO-T", points: [
        "CMO-R: precedes worsening/improvement; warning-stimulus effect.",
        "CMO-T: makes ANOTHER stimulus more (less) effective; interrupted-chain mand training.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "MOs are just discriminative stimuli.",
      reality: "SDs are related to differential AVAILABILITY of a reinforcer; MOs are related to differential EFFECTIVENESS. The two roles can overlap but are conceptually distinct.",
      sourceHint: "SDs are related to the differential availability of a currently effective form of reinforcement for a particular type of behavior, whereas MOs are related to the differential reinforcing effectiveness of a particular event.",
    },
    {
      myth: "Once a behavior is acquired it should generalize regardless of MO.",
      reality: "Acquired behavior may not occur in a new setting if the relevant MO is absent. Programming for generalization must include MOs, not just SDs.",
      sourceHint: "When programming for generalization, clinicians must do so not only across different discriminative stimuli but also across different MOs.",
    },
    {
      myth: "Warning stimuli in escape-avoidance are SDs.",
      reality: "Warning stimuli are CMO-Rs. They fail the SD test because in their absence there is no reinforcer to be unavailable; instead they establish their own offset as a reinforcer.",
      sourceHint: "The CMO-R has usually been interpreted as an SD. The CMO-R fails to qualify as an SD, however, because in its absence there is no MO for a reinforcer that could be unavailable.",
    },
    {
      myth: "MOs and SDs always operate independently.",
      reality: "MOs can indirectly affect the evocative or abative strength of relevant SDs, and value-altering effects extend to conditioned stimuli paired with the relevant consequence.",
      sourceHint: "The behavior-altering effect of the MO can directly increase or decrease the frequency of behavior. It can also indirectly affect the evocative or abative strength of relevant discriminative stimuli.",
    },
    {
      myth: "Most environmental events have only one effect.",
      reality: "Most events have multiple effects. Painful stimulation is an MO (evocative for escape) AND a punisher (reduces future frequency of the response that preceded it).",
      sourceHint: "Most environmental events have more than one effect upon behavior. Events that have MO evocative effects, such as painful stimulation, will also function as punishment for the response immediately preceding the onset of the event.",
    },
  ],
  examChecklist: [
    "Define an MO and state both effects (value-altering and behavior-altering).",
    "Distinguish EO and AO.",
    "Distinguish evocative and abative behavior-altering effects.",
    "State the SD-vs-MO distinction using one example.",
    "List five UMOs and the EO/AO pair for each.",
    "Define and contrast CMO-S, CMO-R, and CMO-T.",
    "Describe the interrupted-chain procedure and identify the MO at work.",
    "Explain how MOs modulate punishment and discriminative stimuli.",
    "Explain why generalization programming must include MOs.",
    "Distinguish behavior-altering (current frequency) from function-altering (future frequency) effects.",
  ],
  flashcardTerms: [
    "Motivating operation (MO)",
    "Establishing operation (EO)", "Abolishing operation (AO)",
    "Value-altering effect", "Behavior-altering effect",
    "Evocative effect", "Abative effect",
    "Unconditioned motivating operation (UMO)",
    "Conditioned motivating operation (CMO)",
    "Surrogate CMO (CMO-S)", "Reflexive CMO (CMO-R)", "Transitive CMO (CMO-T)",
    "Interrupted-chain procedure",
    "Function-altering effect",
  ],
  quiz: [
    {
      id: "Q1",
      question: "An MO has TWO effects simultaneously. They are:",
      options: [
        { label: "A", text: "Discriminative and motivating" },
        { label: "B", text: "Value-altering and behavior-altering" },
        { label: "C", text: "Evocative and discriminative" },
        { label: "D", text: "Conditioned and unconditioned" },
      ],
      answer: "B",
      explanation: "MOs alter the value of the reinforcer/punisher (value-altering) AND the current frequency of behavior consequated by it (behavior-altering).",
      topic: "Definition",
      sourceHint: "A motivating operation (MO) (a) alters the effectiveness of some stimulus as a reinforcer, the value-altering effect; and (b) alters the current frequency of all behavior that has been reinforced by that stimulus, the behavior-altering effect.",
    },
    {
      id: "Q2",
      question: "Food satiation is best classified as:",
      options: [
        { label: "A", text: "An EO with an evocative effect" },
        { label: "B", text: "An AO with an abative effect" },
        { label: "C", text: "A CMO-T" },
        { label: "D", text: "An SD" },
      ],
      answer: "B",
      explanation: "Satiation decreases food's reinforcing value (AO) and decreases the current frequency of food-related behavior (abative).",
      topic: "EO vs AO",
      sourceHint: "food ingestion is an AO and has abative effects on relevant behavior.",
    },
    {
      id: "Q3",
      question: "Withholding a spoon the child needs to eat yogurt creates which type of MO?",
      options: [
        { label: "A", text: "UMO" },
        { label: "B", text: "CMO-S" },
        { label: "C", text: "CMO-R" },
        { label: "D", text: "CMO-T" },
      ],
      answer: "D",
      explanation: "The withholding establishes ANOTHER stimulus (the spoon) as a more potent reinforcer; this is a CMO-T, the basis for interrupted-chain mand training.",
      topic: "CMO-T",
      sourceHint: "The manipulation of CMO-Ts is common practice when teaching mands to individuals with disabilities. The typical procedure, termed interrupted-chain, involves withholding an item the learner needs.",
    },
    {
      id: "Q4",
      question: "Which is the KEY distinction between an SD and an MO?",
      options: [
        { label: "A", text: "SDs are conditioned; MOs are unconditioned" },
        { label: "B", text: "SDs are related to reinforcer AVAILABILITY; MOs are related to reinforcer EFFECTIVENESS" },
        { label: "C", text: "SDs are antecedents; MOs are consequences" },
        { label: "D", text: "SDs apply only to operant behavior" },
      ],
      answer: "B",
      explanation: "SDs signal availability of a currently effective reinforcer; MOs alter how effective the reinforcer is right now.",
      topic: "SD vs MO",
      sourceHint: "SDs are related to the differential availability of a currently effective form of reinforcement for a particular type of behavior, whereas MOs are related to the differential reinforcing effectiveness of a particular event.",
    },
    {
      id: "Q5",
      question: "A warning stimulus that precedes an aversive event in an escape-avoidance procedure is best classified as:",
      options: [
        { label: "A", text: "SD" },
        { label: "B", text: "CMO-R" },
        { label: "C", text: "CMO-S" },
        { label: "D", text: "UMO" },
      ],
      answer: "B",
      explanation: "The warning stimulus's onset becomes a reflexive CMO (CMO-R); its offset functions as reinforcement, and it evokes behavior that has accomplished that offset.",
      topic: "CMO-R",
      sourceHint: "A stimulus that acquires MO effectiveness by preceding some form of worsening or improvement is called a reflexive CMO (CMO-R). It is exemplified by the warning stimulus in a typical escape-avoidance procedure.",
    },
    {
      id: "Q6",
      question: "An EO related to PUNISHMENT would:",
      options: [
        { label: "A", text: "Make a punisher more effective and have an abative effect" },
        { label: "B", text: "Make a reinforcer more effective and have an evocative effect" },
        { label: "C", text: "Make a punisher less effective and have an evocative effect" },
        { label: "D", text: "Make a reinforcer less effective and have an abative effect" },
      ],
      answer: "A",
      explanation: "An EO for punishment establishes punisher value AND abates the behavior that has been punished by that stimulus.",
      topic: "MOs for punishment",
      sourceHint: "An EO related to punishment would have a punisher-establishing effect and an abative effect.",
    },
    {
      id: "Q7",
      question: "Acquired behavior may fail to generalize to a new setting if:",
      options: [
        { label: "A", text: "The new setting contains no people" },
        { label: "B", text: "The relevant MO is absent in the new setting" },
        { label: "C", text: "The behavior was learned too quickly" },
        { label: "D", text: "The behavior was reinforced on a VR schedule" },
      ],
      answer: "B",
      explanation: "Generalization can fail because the relevant MO is missing, not just because new SDs are present.",
      topic: "Generalization",
      sourceHint: "An acquired behavior may not occur in the future in a new setting if the MO is absent.",
    },
  ],
  reviewPrompts: [
    "Identify one EO and one AO in your own life and the evocative/abative effects each produces.",
    "Distinguish CMO-S, CMO-R, and CMO-T with original examples.",
    "Apply the SD vs MO test to a stimulus in your clinical or teaching work.",
    "Sketch an interrupted-chain mand-training procedure with the CMO-T explicitly labeled.",
    "Explain why a behavior that works in clinic may fail at home in terms of MO absence.",
    "Describe how time-out functions as negative punishment in terms of MOs for the reinforcers being made less available.",
  ],
  recap: [
    "Motivating operations have two effects: they ALTER the effectiveness of a reinforcer or punisher (value-altering) AND they ALTER the current frequency of behavior consequated by that stimulus (behavior-altering). EOs increase value and evoke; AOs decrease value and abate.",
    "Most MOs fail the SD test because in their absence there is no reinforcer to be unavailable. UMOs cover deprivation/satiation states and pain/temperature. CMOs come in three types: surrogate (paired), reflexive (warning-stimulus offset), and transitive (alter value of OTHER stimuli).",
    "MOs modulate the three-term contingency. Failing to consider MOs explains preference-assessment shifts, failure to generalize, ineffective skill instruction, and the rise of challenging behavior. Manipulating MOs (especially CMO-Ts via interrupted chains) is the engine of effective mand teaching.",
  ],
  sourceExcerpts: [
    {
      heading: "Definition (Summary §1)",
      text: "A motivating operation (MO) (a) alters the effectiveness of some stimulus as a reinforcer, the value-altering effect; and (b) alters the current frequency of all behavior that has been reinforced by that stimulus, the behavior-altering effect.",
    },
    {
      heading: "SD vs MO",
      text: "SDs are related to the differential availability of a currently effective form of reinforcement for a particular type of behavior, whereas MOs are related to the differential reinforcing effectiveness of a particular event. In nontechnical terms, an SD tells you that something you want is available; an MO makes you want something.",
    },
    {
      heading: "Three CMOs",
      text: "The surrogate CMO (CMO-S) is a stimulus that acquires its MO effectiveness by being paired with another MO, and has the same value-altering and behavior-altering effects as the MO with which it was paired. A stimulus that acquires MO effectiveness by preceding some form of worsening or improvement is called a reflexive CMO (CMO-R). An environmental variable that establishes (or abolishes) the reinforcing effectiveness of another stimulus and evokes (or abates) the behavior that has been reinforced by that other stimulus is a transitive CMO, or CMO-T.",
    },
    {
      heading: "MOs and generalization",
      text: "An acquired behavior may not occur in the future in a new setting if the MO is absent. Thus, it is possible that generalization and maintenance failures are not only a function of novel (generalization) stimuli being dissimilar to those present during training, but also due to different MOs. When programming for generalization, clinicians must do so not only across different discriminative stimuli but also across different MOs.",
    },
  ],
};

export default chapter16Module;
